import React, {useEffect, useState} from 'react';
import {Alert, Button, Card, Col, Flex, Input, Row, Space, Spin, Typography, message} from 'antd';
import {CalendarOutlined, DownloadOutlined, EnvironmentOutlined, SaveOutlined} from '@ant-design/icons';
import {downloadFileWithParams, getRequestWithParams, postRequest, putRequest, putRequestWithParams} from '../../../api/httpRequest';
import {useLocation} from "react-router";

const {Title, Text, Paragraph} = Typography;

interface PlaceDetails {
    id: number;
    city: string;
    university: string;
    address: string;
}

interface SecondTourResult {
    placeId: number;
}

interface SchoolData {
    schoolName: string;
    schoolNumber: string;
}

const SecondTourTab: React.FC = () => {
    const [placeDetails, setPlaceDetails] = useState<PlaceDetails | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [schoolData, setSchoolData] = useState<SchoolData>({schoolName: '', schoolNumber: ''});
    const [originalSchoolData, setOriginalSchoolData] = useState<SchoolData>({schoolName: '', schoolNumber: ''});
    const [saving, setSaving] = useState(false);
    const [isDataSaved, setIsDataSaved] = useState(false);
    const location = useLocation();

    const olympiadId = location.state?.olympiad?.id;

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                
                if (!olympiadId) {
                    throw new Error('Olympiad ID not found');
                }

                const [secondTourResponse, schoolResponse] = await Promise.all([
                    getRequestWithParams('/server/olympiad/result/second', {olympiadId}),
                    getRequestWithParams('/server/olympiad/participant', {olympiadId})
                ]);

                const secondTourData: SecondTourResult = secondTourResponse.data;
                
                if (schoolResponse.data) {
                    const loadedData = {
                        schoolName: schoolResponse.data.schoolName || '',
                        schoolNumber: schoolResponse.data.schoolNumber || ''
                    };
                    setSchoolData(loadedData);
                    setOriginalSchoolData(loadedData);
                    setIsDataSaved(true);
                }

                const placeResponse = await getRequestWithParams('/server/olympiad/place/details', {
                    placeId: secondTourData.placeId
                });
                
                setPlaceDetails(placeResponse.data);
                setError(null);
            } catch (err) {
                setError('Не удалось загрузить информацию о месте проведения');
                console.error('Ошибка при загрузке данных:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [olympiadId]);

    const handleSaveSchoolData = async () => {
        if (!schoolData.schoolName.trim() || !schoolData.schoolNumber.trim()) {
            message.warning('Заполните оба поля: номер и наименование школы');
            return;
        }

        setSaving(true);
        try {
            await putRequestWithParams('/server/olympiad/participant/school', {
                olympiadId,
                schoolName: schoolData.schoolName.trim(),
                schoolNumber: schoolData.schoolNumber.trim()
            });
            setOriginalSchoolData(schoolData);
            setIsDataSaved(true);
            message.success('Данные школы успешно сохранены');
        } catch (err) {
            message.error('Не удалось сохранить данные школы');
            console.error('Ошибка при сохранении данных школы:', err);
        } finally {
            setSaving(false);
        }
    };

    const handleDownload = (filename: string) => {
        downloadFileWithParams("/server/olympiad/files", {
            fileName: filename,
            olympiadId
        })
        .then(response => {
            const href = URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = href;
            link.setAttribute('download', `${filename}`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(href);
        })
        .catch(error => {
            console.error('Ошибка при загрузке файла:', error);
            alert('Не удалось загрузить файл. Пожалуйста, попробуйте еще раз.');
        });
    };

    const isSchoolDataValid = schoolData.schoolName.trim() && schoolData.schoolNumber.trim();
    const isDataChanged = schoolData.schoolName !== originalSchoolData.schoolName || 
                         schoolData.schoolNumber !== originalSchoolData.schoolNumber;

    if (loading) {
        return (
            <div style={{padding: '16px 0', textAlign: 'center'}}>
                <Spin size="large"/>
                <div style={{marginTop: 16}}>Загрузка информации о месте проведения...</div>
            </div>
        );
    }

    const getSecondTourDate = () => {
        console.log(olympiadId)
        if (olympiadId === '1' || olympiadId === '3') {
            return "14.02.2026"
        } else {
            return "15.02.2026"
        }
    }


    if (error) {
        return (
            <div style={{padding: '16px 0'}}>
                <Alert
                    message="Ошибка"
                    description={error}
                    type="error"
                    showIcon
                    style={{marginBottom: 16}}
                />
                <Title level={4}>Информация о заключительном туре</Title>
                
                <Card size="small" style={{marginBottom: 16}}>
                    <Paragraph>
                        {`Поздравляем! Вы прошли в заключительный тур олимпиады, который состоится ${getSecondTourDate()} года по выбранному Вами адресу в 10 часов по местному времени.`}
                    </Paragraph>
                    <Paragraph>
                        Для участия в заключительном (очном) туре необходимо заблаговременно явится по указанному адресу. С собой необходимо иметь следующие документы:
                    </Paragraph>
                    <ul>
                        <li>Паспорт (для идентификации личности);</li>
                        <li>Справка из учебного заведения, о том, что Вы являетесь её учащимся;</li>
                        <li>Подписанное согласие на обработку персональных данных;</li>
                        <li>Распечатанный титульный лист;</li>
                        <li>Распечатанные листы ответа (8-10 шт);</li>
                        <li>Канцелярские принадлежности.</li>
                    </ul>
                    <Paragraph>
                        Титульный лист и листы устного ответа можно распечатать здесь (см. ниже).
                    </Paragraph>
                    <Paragraph>
                        Ваш регистрационный номер будет указан на титульном листе и листах устного ответа. Обращаем Ваше внимание, что регистрационные номера на титуле и листах устного ответа должны совпадать. Список победителей и призеров заключительного (очного) тура олимпиады будет также представлен в форме перечня регистрационных номеров.
                    </Paragraph>
                    <Paragraph>
                        Рекомендуем всем участникам ещё раз проверить правильность введенных при регистрации данных (доступны во вкладке «Обо мне» и «Персональная информация»): фамилию, имя, отчество, дату рождения, паспортные данные, наименование и номер школы.
                    </Paragraph>
                    <Paragraph>
                        Свои вопросы Вы можете задать по электронной почте: fdp@rut-miit.ru или телефону: 8(495)260-23-32.
                    </Paragraph>
                </Card>

                <Card title="Место проведения" size="small">
                    <Text>Информация о месте проведения временно недоступна. Пожалуйста, попробуйте позже.</Text>
                </Card>
            </div>
        );
    }

    return (
        <div style={{padding: '16px 0'}}>
            <Title level={4}>Информация о заключительном туре</Title>
            
            <Card size="small" style={{marginBottom: 16}}>
                <Paragraph>
                    {`Поздравляем! Вы прошли в заключительный тур олимпиады, который состоится ${getSecondTourDate()} года по выбранному Вами адресу в 10 часов по местному времени.`}
                </Paragraph>
                <Paragraph>
                    Для участия в заключительном (очном) туре необходимо заблаговременно явится по указанному адресу. С собой необходимо иметь следующие документы:
                </Paragraph>
                <ul>
                    <li>Паспорт (для идентификации личности);</li>
                    <li>Справка из учебного заведения, о том, что Вы являетесь её учащимся;</li>
                    <li>Подписанное согласие на обработку персональных данных;</li>
                    <li>Распечатанный титульный лист;</li>
                    <li>Распечатанные листы ответа (8-10 шт);</li>
                    <li>Канцелярские принадлежности.</li>
                </ul>
                <Paragraph>
                    Титульный лист и листы устного ответа можно распечатать здесь (см. ниже).
                </Paragraph>
                <Paragraph>
                    Ваш регистрационный номер будет указан на титульном листе и листах устного ответа. Обращаем Ваше внимание, что регистрационные номера на титуле и листах устного ответа должны совпадать. Список победителей и призеров заключительного (очного) тура олимпиады будет также представлен в форме перечня регистрационных номеров.
                </Paragraph>
                <Paragraph>
                    Рекомендуем всем участникам ещё раз проверить правильность введенных при регистрации данных (доступны во вкладке «Обо мне» и «Персональная информация»): фамилию, имя, отчество, дату рождения, паспортные данные, наименование и номер школы.
                </Paragraph>
                <Paragraph>
                    Свои вопросы Вы можете задать по электронной почте: fdp@rut-miit.ru или телефону: 8(495)260-23-32.
                </Paragraph>
            </Card>

            <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                    <Card title="Даты проведения" size="small">
                        <Flex align="center" gap="small">
                            <CalendarOutlined/>
                            <Text>{location.state.olympiad.period}</Text>
                        </Flex>
                    </Card>
                </Col>
                <Col xs={24} md={12}>
                    <Card title="Формат" size="small">
                        <Text>Очный этап</Text>
                    </Card>
                </Col>
                <Col span={24}>
                    <Card title="Место проведения" size="small">
                        <Space direction="vertical" size="middle" style={{width: '100%'}}>
                            {placeDetails ? (
                                <>
                                    <div>
                                        <Text strong>Город: </Text>
                                        <Text>{placeDetails.city}</Text>
                                    </div>
                                    <div>
                                        <Text strong>Аббревиатура ВУЗа: </Text>
                                        <Text>{placeDetails.university}</Text>
                                    </div>
                                    <div>
                                        <Text strong>Адрес: </Text>
                                        <Text>{placeDetails.address}</Text>
                                    </div>
                                </>
                            ) : (
                                <Text>Информация о месте проведения недоступна</Text>
                            )}
                            <Flex gap="small" wrap="wrap">
                                <Button
                                    icon={<EnvironmentOutlined/>}
                                    size="small"
                                    onClick={() => window.open(`https://yandex.ru/maps/?text=${placeDetails?.address}`, '_blank')}
                                >
                                    на карте
                                </Button>
                            </Flex>
                        </Space>
                    </Card>
                </Col>
                <Col span={24}>
                    <Card title="Печать бланков" size="small">
                        <Space direction="vertical" size="middle" style={{width: '100%'}}>
                            <div>
                                <Text strong>Данные вашей школы: </Text>
                                <Flex gap="small" align="flex-start" style={{marginTop: 8}} wrap="wrap">
                                    <Space direction="vertical" size={4} style={{width: 180}}>
                                        <Text style={{fontSize: '12px', fontWeight: 500}}>Номер школы:</Text>
                                        <Input
                                            placeholder="Введите номер школы"
                                            value={schoolData.schoolNumber}
                                            onChange={(e) => {
                                                setSchoolData({...schoolData, schoolNumber: e.target.value});
                                                setIsDataSaved(false);
                                            }}
                                        />
                                    </Space>
                                    <Space direction="vertical" size={4} style={{flex: 1, minWidth: 200}}>
                                        <Text style={{fontSize: '12px', fontWeight: 500}}>Наименование школы:</Text>
                                        <Input
                                            placeholder="Введите наименование школы"
                                            value={schoolData.schoolName}
                                            onChange={(e) => {
                                                setSchoolData({...schoolData, schoolName: e.target.value});
                                                setIsDataSaved(false);
                                            }}
                                        />
                                    </Space>
                                    <div style={{alignSelf: 'flex-end'}}>
                                        <Button
                                            icon={<SaveOutlined/>}
                                            type="primary"
                                            onClick={handleSaveSchoolData}
                                            loading={saving}
                                            disabled={!isSchoolDataValid || !isDataChanged}
                                        >
                                            Сохранить
                                        </Button>
                                    </div>
                                </Flex>
                                {!isDataSaved && (
                                    <Text type="warning" style={{display: 'block', marginTop: 8, fontSize: '12px'}}>
                                        Для печати бланков необходимо заполнить оба поля и нажать "Сохранить"
                                    </Text>
                                )}
                            </div>
                            <Flex gap="small" wrap="wrap">
                                <Button
                                    icon={<DownloadOutlined/>}
                                    type="primary"
                                    onClick={() => handleDownload('title.docx')}
                                    disabled={!isDataSaved}
                                >
                                    Распечатать титульный лист
                                </Button>
                                <Button
                                    icon={<DownloadOutlined/>}
                                    type="primary"
                                    onClick={() => handleDownload('blank.docx')}
                                    disabled={!isDataSaved}
                                >
                                    Распечатать бланк устного ответа
                                </Button>
                                <Button
                                    icon={<DownloadOutlined/>}
                                    type="primary"
                                    onClick={() => handleDownload('consent.docx')}
                                >
                                    Согласие на обработку персональных данных
                                </Button>
                            </Flex>
                        </Space>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default SecondTourTab;